import { RemoteConfigProperties } from "../../../../..";
import { NotImplementedError } from "../../../../../errors";
import { parseDateFilter, parseReportTypeFilter, requireAdvertiserIdFilter, requireAnalysisInterval } from "../../../../../models/looker/modules/attribution";
import { toEmbedHash } from "../../../../../models/looker/query";
import { PromiseHandler } from "../../../../../handlers";
import { FILTER_OPTIONS_CAMPAIGN_QUERY } from "../query";
export default class LookerAttributionCampaignFilter extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-attribution-campaign-filter");
    }
    async findItems(filters) {
        const { reportTypes, lookerMediaTypeFilter } = parseReportTypeFilter(filters.where);
        // cross-channel/filter-options only accepts VIDEO/AUDIO/DISPLAY as
        // report_type — LINEAR has no campaign concept there (campaigns are a
        // digital idea; linear's analogue is Commercial's creative_id/name). A
        // LINEAR selection stays on the Looker path regardless of the flag,
        // rather than earning a 400 the caller never used to see.
        if (this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.REPORTING_API_ATTRIBUTION_FILTERS) &&
            !reportTypes.includes("LINEAR")) {
            return this.findItemsFromReportingApi(filters);
        }
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const parsedDateFilter = parseDateFilter(dateFilter);
        const advertiserIdFilter = filters.where?.find((filter) => filter.field === "advertiser")?.value;
        if (!advertiserIdFilter) {
            throw new Error("Advertiser ID filter is required");
        }
        const queryBody = {
            ...FILTER_OPTIONS_CAMPAIGN_QUERY,
            filters: {
                "conversion_filter_suggests.filter_date_nyc": parsedDateFilter,
                "conversion_filter_suggests.advertiser_id": advertiserIdFilter,
                ...lookerMediaTypeFilter
            }
        };
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatCampaignResponse(data),
                queryMeta
            }
        ])
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch conversion dashboard campaign filter options");
        }));
    }
    /**
     * The same rows through the reporting API instead of the Looker SDK, in the
     * same shape, so no caller can tell which source answered. `campaignIdToLineItemIds`
     * is a mad-sdk-only convenience the endpoint does not build server-side, so
     * it is still assembled here from the flat rows, exactly as it is from Looker.
     */
    async findItemsFromReportingApi(filters) {
        const analysisInterval = requireAnalysisInterval(filters.where);
        const advertiserIdFilter = requireAdvertiserIdFilter(filters.where);
        const { reportTypes } = parseReportTypeFilter(filters.where);
        const request = {
            fields: ["campaign_id", "campaign_name", "lineitem_id"],
            filters: {
                analysis_interval: analysisInterval,
                advertiser_id: advertiserIdFilter,
                report_type: reportTypes.join(",")
            },
            sorts: ["campaign_name desc"],
            limit: -1
        };
        const hashKey = `api:${await this.sdk.looker.query.hashQuery(request)}`;
        return this.cache.promise(hashKey, () => this.sdk.looker.reporting
            .query("/attribution/cross-channel/filter-options", request, "Failed to fetch conversion dashboard campaign filter options.")
            .then(({ data, query }) => [
            {
                data: this.formatCampaignApiResponse(data),
                queryMeta: { embedHash: toEmbedHash(query?.share_url) }
            }
        ]));
    }
    formatCampaignApiResponse(rows) {
        const campaignIdToLineItemIds = {};
        const seenCampaignIds = new Set();
        const campaigns = [];
        rows.forEach((row) => {
            const campaignId = row.campaign_id;
            const campaignName = row.campaign_name;
            const lineItemId = row.lineitem_id;
            if (campaignId) {
                if (!seenCampaignIds.has(campaignId)) {
                    campaigns.push({
                        name: campaignName ?? "",
                        id: campaignId
                    });
                    seenCampaignIds.add(campaignId);
                }
                if (lineItemId) {
                    if (!campaignIdToLineItemIds[campaignId]) {
                        campaignIdToLineItemIds[campaignId] = new Set();
                    }
                    campaignIdToLineItemIds[campaignId].add(lineItemId);
                }
            }
        });
        return {
            campaigns,
            campaignIdToLineItemIds
        };
    }
    formatCampaignResponse(data) {
        const campaignIdToLineItemIds = {};
        const seenCampaignIds = new Set();
        const campaigns = [];
        data.forEach((campaign) => {
            const campaignId = campaign["conversion_filter_suggests.campaign_id"];
            const campaignName = campaign["conversion_filter_suggests.campaign_name"];
            const lineItemId = campaign["conversion_filter_suggests.lineitem_id"];
            if (campaignId) {
                if (!seenCampaignIds.has(campaignId)) {
                    campaigns.push({
                        name: campaignName,
                        id: campaignId
                    });
                    seenCampaignIds.add(campaignId);
                }
                if (!campaignIdToLineItemIds[campaignId]) {
                    campaignIdToLineItemIds[campaignId] = new Set();
                }
                campaignIdToLineItemIds[campaignId].add(lineItemId);
            }
        });
        return {
            campaigns,
            campaignIdToLineItemIds
        };
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=Campaign.js.map